import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useHttpClient, type HttpResponse } from '@bonusly/http-client';

import { RecognizeResponse } from '../types/api';
import { BonusReport } from '../types/bonus-report';
import { toSnakeCaseKeys } from '../utils/object';
import { ResourcePath } from './resource-path';
import { MutationOptions, QueryKey } from './types';

// --- Hidden Bonus IDs (merged global + private) ---

interface HiddenBonusIdsResponse {
	result: { bonus_ids: string[] };
}

export const useHiddenBonusIds = () => {
	const http = useHttpClient('v2');
	return useQuery([QueryKey.HiddenBonusIds], async () => {
		const res = await http.get<HiddenBonusIdsResponse>(ResourcePath.HiddenBonusIds);
		return res.data.result.bonus_ids;
	});
};

// --- Create Bonus Report (report to admins, prose-only) ---

export interface BonusReportCreateArgs {
	bonusReport: {
		bonusId: string;
		userInput: string;
	};
}

export type BonusReportCreateResponseData = RecognizeResponse<BonusReport>;

export type BonusReportCreateResponse = HttpResponse<BonusReportCreateResponseData>;

export const useCreateBonusReport = (options: MutationOptions<BonusReportCreateArgs, BonusReportCreateResponse>) => {
	const http = useHttpClient('v2');
	const queryClient = useQueryClient();

	return useMutation(
		(args: BonusReportCreateArgs) =>
			http.post<BonusReportCreateResponseData, BonusReportCreateResponse>(
				ResourcePath.BonusReports,
				toSnakeCaseKeys({
					bonusReport: {
						bonusId: args.bonusReport.bonusId,
						reason: 'other',
						userInput: args.bonusReport.userInput,
					},
				})
			),
		{
			...options,
			onSuccess: (data, variables, context) => {
				queryClient.setQueryData<string[]>([QueryKey.HiddenBonusIds], (old) => {
					return [...(old ?? []), variables.bonusReport.bonusId];
				});
				queryClient.invalidateQueries([QueryKey.HiddenBonusIds]);
				options?.onSuccess?.(data, variables, context);
			},
		}
	);
};

// --- Create Private Hide (hide from me only) ---

export interface PrivateHideCreateArgs {
	bonusId: string;
}

export const useCreatePrivateHide = (options?: MutationOptions<PrivateHideCreateArgs, HttpResponse>) => {
	const http = useHttpClient('v2');
	const queryClient = useQueryClient();

	return useMutation((args: PrivateHideCreateArgs) => http.post(ResourcePath.PrivateHides, { bonus_id: args.bonusId }), {
		...options,
		onSuccess: (data, variables, context) => {
			queryClient.setQueryData<string[]>([QueryKey.HiddenBonusIds], (old) => {
				return [...(old ?? []), variables.bonusId];
			});
			queryClient.invalidateQueries([QueryKey.HiddenBonusIds]);
			options?.onSuccess?.(data, variables, context);
		},
	});
};
